// Static theme configuration - no React context needed
// This provides MUI-compatible structure for components

export const themeConfig = {
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#fff',
    },
    secondary: {
      main: '#9c27b0',
      light: '#ba68c8',
      dark: '#7b1fa2',
      contrastText: '#fff',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
      contrastText: '#fff',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
      contrastText: '#fff',
    },
    info: {
      main: '#0288d1',
      light: '#03a9f4',
      dark: '#01579b',
      contrastText: '#fff',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
      contrastText: '#fff',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    background: {
      default: '#fff',
      paper: '#fff',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
  },
  spacing: factor => `${0.25 * factor}rem`,
  shape: {
    borderRadius: 4,
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
  ],
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontSize: '6rem', fontWeight: 300, lineHeight: 1.167 },
    h2: { fontSize: '3.75rem', fontWeight: 300, lineHeight: 1.2 },
    h3: { fontSize: '3rem', fontWeight: 400, lineHeight: 1.167 },
    h4: { fontSize: '2.125rem', fontWeight: 400, lineHeight: 1.235 },
    h5: { fontSize: '1.5rem', fontWeight: 400, lineHeight: 1.334 },
    h6: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 },
    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.43 },
  },
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
    up: key => `@media (min-width:${themeConfig.breakpoints.values[key]}px)`,
    down: key => {
      const keys = Object.keys(themeConfig.breakpoints.values);
      const index = keys.indexOf(key);
      if (index === keys.length - 1) return '@media (min-width:0px)';
      return `@media (max-width:${themeConfig.breakpoints.values[keys[index + 1]] - 0.05}px)`;
    },
  },
  // Direction - can be overridden by settings
  direction: 'ltr',
};

// Helper function for alpha (opacity)
export function alpha(color, value) {
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${value})`;
  }
  // Already rgba
  return color.replace(/[\d.]+\)$/g, `${value})`);
}

// Hook for backwards compatibility - returns static theme with direction from settings
export function useTheme() {
  return themeConfig;
}

export default themeConfig;
